import React, { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Plus, Mail } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function FoldersPage() {
  const { user } = useAuth();
  const [folders, setFolders] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ team_id: "", name: "", username: "", description: "" });
  const [err, setErr] = useState("");

  const load = async () => {
    const [f, t] = await Promise.all([api.get("/folders"), api.get("/teams")]);
    setFolders(f.data);
    setTeams(t.data);
    if (!form.team_id && t.data[0]) setForm((f0) => ({ ...f0, team_id: t.data[0].id }));
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/folders", form);
      setOpen(false);
      setForm({ team_id: teams[0]?.id || "", name: "", username: "", description: "" });
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail));
    }
  };

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";

  const canCreate = user.role === "super_admin" || user.role === "team_admin";

  return (
    <AppShell
      title="Folders"
      actions={
        canCreate && (
          <button data-testid="new-folder-button" className="ds-btn-primary flex items-center gap-2" onClick={() => setOpen(true)}>
            <Plus size={14} /> New folder
          </button>
        )
      }
    >
      <div className="ds-card overflow-x-auto">
        <table className="ds-table" data-testid="folders-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Username</th>
              <th>Team</th>
              <th>Scan email</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {folders.map((f) => (
              <tr key={f.id} data-testid={`folder-row-${f.id}`}>
                <td className="font-semibold">{f.name}</td>
                <td className="font-mono text-[12px]">{f.username}</td>
                <td>{teamName(f.team_id)}</td>
                <td>
                  <span className="font-mono text-[12px] text-[#026AA2] inline-flex items-center gap-1">
                    <Mail size={11} /> {f.scan_email}
                  </span>
                </td>
                <td className="text-[12px] text-[#4A4A52]">{f.description || "—"}</td>
              </tr>
            ))}
            {folders.length === 0 && (
              <tr><td colSpan={5} className="text-center text-[#8A8A93] py-8">No folders yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="ds-card p-6 w-full max-w-md" data-testid="new-folder-form">
            <h3 className="font-display text-lg font-bold mb-4">Create folder</h3>
            <div className="space-y-3">
              <div>
                <label className="ds-label">Team</label>
                <select className="ds-input" value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} data-testid="new-folder-team">
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
              <div>
                <label className="ds-label">Name</label>
                <input className="ds-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-folder-name" />
              </div>
              <div>
                <label className="ds-label">Username (unique, used for scan email)</label>
                <input className="ds-input" required pattern="[a-zA-Z0-9._-]+" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="new-folder-username" />
              </div>
              <div>
                <label className="ds-label">Description</label>
                <input className="ds-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              {err && <div className="text-[12px] text-[#B42318]">{err}</div>}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="ds-btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
              <button type="submit" className="ds-btn-primary" data-testid="new-folder-submit">Create</button>
            </div>
          </form>
        </div>
      )}
    </AppShell>
  );
}
